/**
 * Hard-mode sanctuary: weathered lighthouse with rotating beacon.
 * When the beam sweeps over the bird, restore one zap (recovery point).
 */

export function createLighthouse(boardW, boardH, opts) {
  const options = opts || {};
  const groundY =
    options.groundY != null ? options.groundY : boardH * 0.9;
  const x = options.x != null ? options.x : boardW * 0.82;
  return {
    kind: 'lighthouse',
    x,
    groundY,
    towerW: Math.max(36, boardW * 0.055),
    towerH: boardH * 0.72,
    beamAngle: Math.PI + 0.4, // start sweeping across playfield
    beamHalfWidth: 0.24,
    beamRange: Math.max(280, boardW * 0.7),
    beamSpeed: 0.011,
    cooldownUntil: 0,
    healingUntil: 0,
    wasInBeam: false,
    particles: [],
    spray: [],
    waves: seedWaves(),
    drips: [],
    nextDripAt: 0,
    nextSprayAt: 0,
    seed: Math.random() * 100,
  };
}

function seedWaves() {
  const waves = [];
  for (let i = 0; i < 5; i += 1) {
    waves.push({
      phase: Math.random() * Math.PI * 2,
      amp: 3 + Math.random() * 5,
      speed: 0.03 + Math.random() * 0.025,
      yOff: i * 3,
    });
  }
  return waves;
}

export function lanternPos(lh) {
  return {
    x: lh.x,
    y: lh.groundY - lh.towerH * 0.9,
  };
}

/** Safe ledge near the lighthouse base (visual refuge). */
export function ledgePos(lh) {
  return {
    x: lh.x - lh.towerW * 1.35,
    y: lh.groundY - lh.towerH * 0.12,
    w: lh.towerW * 1.4,
    h: lh.towerW * 0.35,
  };
}

export function lighthouseReady(lh, now) {
  return lh && now >= lh.cooldownUntil;
}

/**
 * True when bird lies inside the rotating beacon cone.
 */
export function beamHitsBird(lh, birdCx, birdCy) {
  if (!lh) return false;
  const lan = lanternPos(lh);
  const dx = birdCx - lan.x;
  const dy = birdCy - lan.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 28 || dist > lh.beamRange) return false;
  let ang = Math.atan2(dy, dx);
  let diff = ang - lh.beamAngle;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return Math.abs(diff) <= lh.beamHalfWidth;
}

/**
 * @returns {{ recovered: boolean, illuminated: boolean }}
 */
export function tickLighthouse(lh, now, reducedMotion, birdCx, birdCy, fx) {
  if (!lh) return { recovered: false, illuminated: false };
  const gust = !!(fx && fx.windGust);

  if (!reducedMotion) {
    lh.beamAngle += lh.beamSpeed;
    if (lh.beamAngle > Math.PI * 3) lh.beamAngle -= Math.PI * 2;
  }

  // Sea spray / mist near base
  if (!reducedMotion && now >= lh.nextSprayAt) {
    lh.nextSprayAt = now + (gust ? 120 : 280) + Math.random() * 200;
    for (let i = 0; i < (gust ? 3 : 1); i += 1) {
      lh.spray.push({
        x: lh.x - lh.towerW * (0.8 + Math.random() * 1.6),
        y: lh.groundY - 4,
        vx: -(0.6 + Math.random() * 1.4),
        vy: -(0.8 + Math.random() * 1.8),
        life: 1,
        size: 2 + Math.random() * 3,
      });
    }
  }

  if (!reducedMotion && now >= lh.nextDripAt) {
    lh.nextDripAt = now + 160 + Math.random() * 320;
    lh.drips.push({
      x: lh.x + (Math.random() - 0.5) * lh.towerW * 0.9,
      y: lh.groundY - lh.towerH * (0.2 + Math.random() * 0.6),
      vy: 1.2 + Math.random() * 1.8,
      life: 1,
    });
  }

  const spray = [];
  for (let i = 0; i < lh.spray.length; i += 1) {
    const s = lh.spray[i];
    if (!reducedMotion) {
      s.x += s.vx;
      s.y += s.vy;
      s.vy += 0.04;
      s.life -= 0.02;
    } else {
      s.life -= 0.06;
    }
    if (s.life > 0.05) spray.push(s);
  }
  lh.spray = spray;

  const drips = [];
  for (let i = 0; i < lh.drips.length; i += 1) {
    const d = lh.drips[i];
    if (!reducedMotion) {
      d.y += d.vy;
      d.x -= 0.4;
      d.life -= 0.028;
    } else {
      d.life -= 0.08;
    }
    if (d.life > 0.05 && d.y < lh.groundY + 6) drips.push(d);
  }
  lh.drips = drips;

  const parts = [];
  for (let i = 0; i < lh.particles.length; i += 1) {
    const pt = lh.particles[i];
    if (!reducedMotion) {
      pt.x += pt.vx;
      pt.y += pt.vy;
      pt.vy -= 0.015;
      pt.life -= pt.decay;
    } else {
      pt.life -= 0.06;
    }
    if (pt.life > 0.05) parts.push(pt);
  }
  lh.particles = parts;

  const illuminated = beamHitsBird(lh, birdCx, birdCy);
  let recovered = false;

  // Rising edge: beam just found the bird
  if (illuminated && !lh.wasInBeam && lighthouseReady(lh, now)) {
    recovered = true;
    lh.healingUntil = now + (fx && fx.healMs ? fx.healMs : 900);
    lh.cooldownUntil = now + (fx && fx.cooldownMs ? fx.cooldownMs : 8000);
    // Warm recovery particles around the bird
    for (let i = 0; i < 14; i += 1) {
      const a = (Math.PI * 2 * i) / 14 + Math.random() * 0.3;
      lh.particles.push({
        x: birdCx,
        y: birdCy,
        vx: Math.cos(a) * (0.6 + Math.random() * 1.4),
        vy: Math.sin(a) * (0.6 + Math.random() * 1.2) - 0.8,
        life: 1,
        decay: 0.018 + Math.random() * 0.012,
        size: 2.5 + Math.random() * 3,
      });
    }
  }
  lh.wasInBeam = illuminated;

  return { recovered, illuminated };
}

function drawOcean(p, lh, boardW, boardH, frameCount, flash) {
  const baseY = lh.groundY + 2;
  p.push();
  p.noStroke();
  // Water band
  p.fill(35, 55, 75, 90 + flash * 30);
  p.rect(0, baseY - 8, boardW, Math.max(24, boardH - baseY + 16));
  p.fill(25, 40, 58, 100);
  p.rect(0, baseY + 10, boardW, Math.max(40, boardH - baseY));

  for (let i = 0; i < lh.waves.length; i += 1) {
    const w = lh.waves[i];
    const y = baseY + w.yOff + Math.sin(frameCount * w.speed + w.phase) * w.amp;
    p.stroke(160, 185, 210, 50 + flash * 40);
    p.strokeWeight(1.4);
    p.noFill();
    p.beginShape();
    for (let x = 0; x <= boardW; x += 18) {
      const yy =
        y +
        Math.sin(x * 0.02 + frameCount * 0.04 + w.phase) * 2.5;
      p.vertex(x, yy);
    }
    p.endShape();
  }

  // Rocky shoreline under lighthouse
  p.noStroke();
  p.fill(55, 58, 62, 220);
  p.beginShape();
  p.vertex(lh.x - lh.towerW * 2.4, lh.groundY + 6);
  p.vertex(lh.x - lh.towerW * 1.8, lh.groundY - 10);
  p.vertex(lh.x - lh.towerW * 0.6, lh.groundY - 18);
  p.vertex(lh.x + lh.towerW * 0.9, lh.groundY - 12);
  p.vertex(lh.x + lh.towerW * 1.8, lh.groundY + 4);
  p.vertex(lh.x + lh.towerW * 2.2, lh.groundY + 10);
  p.vertex(lh.x - lh.towerW * 2.6, lh.groundY + 12);
  p.endShape(p.CLOSE);
  p.fill(40, 42, 48, 180);
  p.ellipse(lh.x - lh.towerW * 1.5, lh.groundY + 2, lh.towerW * 1.2, 10);
  p.ellipse(lh.x + lh.towerW * 0.4, lh.groundY + 4, lh.towerW, 8);
  p.pop();
}

function drawBeam(p, lh, flash) {
  const lan = lanternPos(lh);
  const half = lh.beamHalfWidth;
  const range = lh.beamRange;
  const a0 = lh.beamAngle - half;
  const a1 = lh.beamAngle + half;

  p.push();
  p.noStroke();
  // Soft cone cutting through fog
  p.fill(255, 210, 120, 32 + flash * 10);
  p.beginShape();
  p.vertex(lan.x, lan.y);
  p.vertex(lan.x + Math.cos(a0) * range, lan.y + Math.sin(a0) * range);
  p.vertex(lan.x + Math.cos(a1) * range, lan.y + Math.sin(a1) * range);
  p.endShape(p.CLOSE);

  // Brighter core shaft
  p.fill(255, 230, 160, 48 + flash * 12);
  p.beginShape();
  p.vertex(lan.x, lan.y);
  p.vertex(
    lan.x + Math.cos(lh.beamAngle - half * 0.35) * range,
    lan.y + Math.sin(lh.beamAngle - half * 0.35) * range
  );
  p.vertex(
    lan.x + Math.cos(lh.beamAngle + half * 0.35) * range,
    lan.y + Math.sin(lh.beamAngle + half * 0.35) * range
  );
  p.endShape(p.CLOSE);

  // Tip glow at lantern
  p.fill(255, 240, 180, 100);
  p.circle(lan.x, lan.y, 18);
  p.fill(255, 250, 220, 150);
  p.circle(lan.x, lan.y, 8);
  p.pop();
}

/**
 * @param {{ lightningFlash?: number, illuminated?: boolean, healing?: boolean, boardW?: number, boardH?: number }} [atmosphere]
 */
export function drawLighthouse(p, lh, now, frameCount, atmosphere) {
  if (!lh) return;
  const atm = atmosphere || {};
  const flash = Math.max(0, Math.min(1, atm.lightningFlash || 0));
  const healing = !!atm.healing || now < lh.healingUntil;
  const illuminated = !!atm.illuminated;
  const light = 0.55 + flash * 0.45;
  const tw = lh.towerW;
  const th = lh.towerH;
  const boardW = atm.boardW || p.width || lh.x * 1.4;
  const boardH = atm.boardH || p.height || lh.groundY + 40;

  p.push();

  // Mist veil around the structure
  p.noStroke();
  p.fill(40, 55, 75, 40);
  p.ellipse(lh.x - tw, lh.groundY - th * 0.4, tw * 6, th * 0.9);

  drawOcean(p, lh, boardW, boardH, frameCount, flash);

  // Beam under tower silhouette so tower reads on top of shaft near base
  drawBeam(p, lh, flash);

  // —— Refuge ledge ——
  const ledge = ledgePos(lh);
  p.fill(70, 72, 78, 220);
  p.quad(
    ledge.x - ledge.w * 0.5,
    ledge.y,
    ledge.x + ledge.w * 0.5,
    ledge.y - 4,
    ledge.x + ledge.w * 0.45,
    ledge.y + ledge.h,
    ledge.x - ledge.w * 0.55,
    ledge.y + ledge.h + 3
  );
  p.fill(255, 210, 130, healing || illuminated ? 35 : 12);
  p.ellipse(ledge.x, ledge.y - 2, ledge.w * 0.9, 10);

  // —— Tower body (weathered stone / paint bands) ——
  const bands = [
    [210, 205, 195],
    [70, 85, 120],
    [215, 208, 198],
    [65, 80, 115],
    [200, 195, 185],
  ];
  const bandH = th * 0.72 / bands.length;
  for (let i = 0; i < bands.length; i += 1) {
    const [r, g, b] = bands[i];
    const y0 = lh.groundY - th * 0.08 - i * bandH;
    const taper0 = 1 - i * 0.04;
    const taper1 = 1 - (i + 1) * 0.04;
    p.fill(r * light, g * light, b * light, 235);
    p.beginShape();
    p.vertex(lh.x - tw * 0.55 * taper0, y0);
    p.vertex(lh.x + tw * 0.55 * taper0, y0);
    p.vertex(lh.x + tw * 0.55 * taper1, y0 - bandH);
    p.vertex(lh.x - tw * 0.55 * taper1, y0 - bandH);
    p.endShape(p.CLOSE);
  }

  // Base plinth
  p.fill(90 * light, 92 * light, 95 * light, 240);
  p.beginShape();
  p.vertex(lh.x - tw * 0.85, lh.groundY);
  p.vertex(lh.x + tw * 0.85, lh.groundY);
  p.vertex(lh.x + tw * 0.65, lh.groundY - th * 0.1);
  p.vertex(lh.x - tw * 0.65, lh.groundY - th * 0.1);
  p.endShape(p.CLOSE);

  // Cracks / age
  p.stroke(40, 38, 36, 70 + flash * 80);
  p.strokeWeight(1.1);
  for (let i = 0; i < 8; i += 1) {
    const x0 = lh.x - tw * 0.3 + (i % 4) * tw * 0.18;
    const y0 = lh.groundY - th * (0.15 + (i % 5) * 0.12);
    p.line(x0, y0, x0 + Math.sin(i + lh.seed) * 4, y0 - th * 0.12);
  }
  // Rusted rail hint / windows
  p.noStroke();
  p.fill(30, 35, 45, 180);
  for (let i = 0; i < 3; i += 1) {
    const wy = lh.groundY - th * (0.25 + i * 0.18);
    p.rect(lh.x - tw * 0.12, wy, tw * 0.24, th * 0.06, 2);
  }
  // Door
  p.fill(45, 40, 35, 200);
  p.rect(lh.x - tw * 0.14, lh.groundY - th * 0.09 - 4, tw * 0.28, th * 0.09, 2);

  // Gallery / lantern room
  const lan = lanternPos(lh);
  p.fill(120 * light, 110 * light, 95 * light, 230);
  p.rect(lh.x - tw * 0.42, lan.y - 4, tw * 0.84, th * 0.08);
  // Rusted railing
  p.stroke(90, 60, 45, 180);
  p.strokeWeight(1.5);
  p.line(lh.x - tw * 0.48, lan.y, lh.x + tw * 0.48, lan.y);
  p.noStroke();
  // Glass lantern
  p.fill(255, 230, 160, 100 + flash * 40);
  p.rect(lh.x - tw * 0.28, lan.y - th * 0.1, tw * 0.56, th * 0.1, 3);
  p.fill(255, 245, 200, 160);
  p.circle(lan.x, lan.y - th * 0.04, tw * 0.35);
  // Dome / cap
  p.fill(55 * light, 58 * light, 65 * light, 240);
  p.arc(lh.x, lan.y - th * 0.1, tw * 0.7, tw * 0.45, Math.PI, 0, p.CHORD);
  p.fill(70, 55, 40, 200);
  p.rect(lh.x - 3, lan.y - th * 0.18, 6, th * 0.06);

  // Rain drips on tower
  p.strokeWeight(1.2);
  for (let i = 0; i < lh.drips.length; i += 1) {
    const d = lh.drips[i];
    p.stroke(170, 195, 215, Math.floor(150 * d.life));
    p.line(d.x, d.y, d.x - 1.5, d.y + 5);
  }

  // Sea spray
  p.noStroke();
  for (let i = 0; i < lh.spray.length; i += 1) {
    const s = lh.spray[i];
    p.fill(200, 215, 230, Math.floor(120 * s.life));
    p.circle(s.x, s.y, s.size * s.life);
  }

  // Recovery particles
  for (let i = 0; i < lh.particles.length; i += 1) {
    const pt = lh.particles[i];
    p.fill(255, 210, 120, Math.floor(220 * pt.life));
    p.circle(pt.x, pt.y, pt.size * (0.6 + pt.life * 0.5));
  }

  // Ready pulse at lantern when recovery available
  if (lighthouseReady(lh, now) && !healing) {
    const pulse = 0.6 + Math.sin(frameCount * 0.08) * 0.4;
    p.fill(255, 220, 140, Math.floor(35 * pulse));
    p.circle(lan.x, lan.y, tw * 1.2 * pulse);
  }

  p.pop();
}

/** Soft warm wash drawn over the bird while in beam / healing. */
export function drawBirdBeaconGlow(p, birdCx, birdCy, birdW, birdH, strength) {
  if (strength <= 0) return;
  p.push();
  p.noStroke();
  p.fill(255, 215, 130, Math.floor(50 * strength));
  p.ellipse(birdCx, birdCy, birdW * 1.8, birdH * 1.8);
  p.fill(255, 235, 180, Math.floor(40 * strength));
  p.ellipse(birdCx, birdCy, birdW * 1.15, birdH * 1.15);
  p.pop();
}
